import React, { FC } from "react";
import { useIntl } from "react-intl";
import { CustomerMergeSearchResult } from "@Shape-Digital/kudzu-data/lib/types/actions";
import Paper from "../../Unknown/Paper";
import Button from "../../Unknown/Button";
import Box from "../../Unknown/Box";
import Typography from "../../Unknown/Typography";
import Alert from "../../Unknown/Alert";
import AlertTitle from "../../Unknown/AlertTitle";
import CircularProgress from "../../Unknown/CircularProgress";
import CustomerMergeColumn from "./CustomerMergeColumn";
import AddCustomerToMergeSearch from "./AddCustomerToMergeSearch";
import DuplicatePatientGroupsList from "./DuplicatePatientGroupsList";
import {
  CustomerInMerge,
  FieldSelections,
  MergeError,
  MergeFieldKey,
  MergeResult,
} from "./types";
import messages from "./messages";

type MergeCustomersPageContentProps = {
  customers: CustomerInMerge[];
  fieldSelections: FieldSelections;
  hasAnySelfPatient: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  mergeResult: MergeResult | null;
  errorMessage: MergeError | null;
  blockerError: MergeError | null;
  onFieldSelect: (field: MergeFieldKey, customerId: string) => void;
  onAddCustomer: (customer: CustomerMergeSearchResult) => void;
  onRemoveCustomer: (customerId: string) => void;
  onSubmit: () => void;
  // AC19 "Cancel" (no changes made) — returns to where we came from.
  onDone: () => void;
  // AC33/AC34 — "View customer" link on the success view navigates to the
  // selected customer's drawer (NOT wherever the merge screen was opened
  // from, which may now be a merged-away/dead customer).
  onViewCustomer: (selectedCustomerId: string) => void;
  // AC12 — "Wards"/"Family members" footer links navigate to the Patient drawer.
  onPatientClick: (mrn: string) => void;
  // AC37/38 — "View appointment N" links in the Conflict state, opens in a
  // new tab.
  onAppointmentClick: (appointmentId: string) => void;
};

// Matches Figma "Merge Customers" full-page frame (node 11427:31220): a white
// paper section on the app's standard grey page canvas, below the shared
// app header (rendered by AuthenticatedLayout around every route) — not a
// centered modal dialog.
const MergeCustomersPageContent: FC<MergeCustomersPageContentProps> = ({
  customers,
  fieldSelections,
  hasAnySelfPatient,
  canSubmit,
  isSubmitting,
  mergeResult,
  errorMessage,
  blockerError,
  onFieldSelect,
  onAddCustomer,
  onRemoveCustomer,
  onSubmit,
  onDone,
  onViewCustomer,
  onPatientClick,
  onAppointmentClick,
}) => {
  const { formatMessage } = useIntl();
  const alreadySelectedIds = customers.map((c) => c.id);
  const canAddMore = customers.length < 4;
  const showAddPlaceholder = canAddMore && customers.length < 2;

  // Success state — Figma "Merge Customers success" spec: alert with a
  // "View customer" link (AC33/34), followed by one row per duplicate
  // Patient group (AC35/36) with a "Merge now" action.
  if (mergeResult) {
    return (
      <Paper sx={{ p: "30px" }}>
        <Alert severity="success" sx={{ mb: "24px" }}>
          <Typography variant="body2">
            {formatMessage(messages.mergedSuccess, {
              name: mergeResult.selectedCustomerName,
            })}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => onViewCustomer(mergeResult.selectedCustomerId)}
            sx={{
              p: 0,
              minWidth: 0,
              color: "inherit",
              textTransform: "none",
              textDecoration: "underline",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {formatMessage(messages.viewCustomer)}
          </Button>
        </Alert>

        {mergeResult.duplicatePatientGroups.length > 0 && (
          <DuplicatePatientGroupsList
            duplicatePatientGroups={mergeResult.duplicatePatientGroups}
            selectedCustomerId={mergeResult.selectedCustomerId}
            selectedCustomerName={mergeResult.selectedCustomerName}
            onPatientClick={onPatientClick}
            onAppointmentClick={onAppointmentClick}
          />
        )}
      </Paper>
    );
  }

  // Loading state
  if (isSubmitting) {
    return (
      <Paper sx={{ p: "30px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 360,
            gap: 5,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" textAlign="center">
            {formatMessage(messages.processing)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: "30px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ flex: "1 1 50%" }}>
          <Typography variant="h5">{formatMessage(messages.title)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.subtitle)}
          </Typography>
        </Box>
        {canAddMore && (
          <Box sx={{ flex: "1 1 50%" }}>
            <AddCustomerToMergeSearch
              alreadySelectedIds={alreadySelectedIds}
              onSelect={onAddCustomer}
            />
          </Box>
        )}
      </Box>

      {/* Selected customers — columns share a single outer border, divided
          by 1px lines (matches Figma "Selected customers" frame). Columns
          never shrink below their minWidth (see CustomerMergeColumn) — once
          more than ~3-4 fit, the row scrolls horizontally instead of
          compressing fields into an unreadable width (rare, but supported
          up to the 4-customer cap). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          overflowX: "auto",
          overflowY: "hidden",
          "& > *": {
            borderRight: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& > *:last-child": { borderRight: 0 },
        }}
      >
        {customers.map((customer) => (
          <CustomerMergeColumn
            key={customer.id}
            customer={customer}
            fieldSelections={fieldSelections}
            onFieldSelect={onFieldSelect}
            showPatientFields={hasAnySelfPatient}
            showRemove={customers.length > 1}
            onRemove={onRemoveCustomer}
            onPatientClick={onPatientClick}
          />
        ))}

        {/* Placeholder cell — only shown while fewer than 2 customers are selected;
            once there are 2+, the search field above is enough (up to 4). */}
        {showAddPlaceholder && (
          <Box
            sx={{
              flex: "1 1 240px",
              flexShrink: 0,
              minWidth: 240,
              p: 3,
              display: "flex",
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: "1px dashed",
                borderColor: "rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                minHeight: 240,
              }}
            >
              <Typography variant="body1" textAlign="center">
                {formatMessage(messages.addAnotherCustomer)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {blockerError ? (
        <Alert severity="error">
          <AlertTitle>{blockerError.title}</AlertTitle>
          {blockerError.description}
        </Alert>
      ) : (
        errorMessage && (
          <Alert severity="error">
            <AlertTitle>{errorMessage.title}</AlertTitle>
            {errorMessage.description}
          </Alert>
        )
      )}

      {canSubmit && !errorMessage && !blockerError && (
        <Alert severity="warning">
          <AlertTitle>{formatMessage(messages.warningTitle)}</AlertTitle>
          {formatMessage(messages.warningDescription)}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button variant="text" onClick={onDone} disabled={isSubmitting}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {formatMessage(messages.merge)}
        </Button>
      </Box>
    </Paper>
  );
};

export default MergeCustomersPageContent;
